/**
 * Enhanced permission parser for basic and team-specific permissions
 * 
 * Supports formats like:
 * - 'read_dashboard' => { action: 'read', subject: 'Dashboard' }
 * - 'create_packages' => { action: 'create', subject: 'Packages' }
 * - 'edit_incoming_parcels' => { action: 'edit', subject: 'IncomingParcels' }
 * - 'read-GD-parcels' => { action: 'read', subject: 'Parcels' }
 * - 'access-GD-packages' => { action: 'access', subject: 'Packages' }
 * - 'create-GD-packing-lists' => { action: 'create', subject: 'PackingLists' }
 * - 'access-TEAM-customers' => { action: 'access', subject: 'Customers' }
 */
export function parsePermission(permission) {
  if (typeof permission === 'string') {
    // Handle team-specific permissions with dash format (e.g., 'read-GD-parcels')
    if (permission.includes('-')) {
      const parts = permission.split('-')
      
      // Check if it follows the pattern: action-teamcode-subject (where subject can have dashes)
      if (parts.length >= 3) {
        const action = parts[0]
        const teamCode = parts[1]
        const subject = parts.slice(2).join('-') // Rejoin remaining parts for compound subjects
        
        // Parse as: action on subject that belongs to team
        // e.g., 'read-GD-parcels' becomes { action: 'read', subject: 'Parcels' }
        // e.g., 'access-GD-packages' becomes { action: 'access', subject: 'Packages' }
        // e.g., 'create-GD-packing-lists' becomes { action: 'create', subject: 'PackingLists' }
        // The team context is implicit - if user has team permission, they can access the subject
        let parsedSubject = subject
        
        // Handle special plural/compound subjects
        if (subject === 'packing-lists' || subject === 'packing_lists') {
          parsedSubject = 'PackingLists'
        } else if (subject === 'container-arrivals' || subject === 'container_arrivals') {
          parsedSubject = 'ContainerArrivals'
        } else if (subject === 'parcels') {
          parsedSubject = 'Parcels'
        } else if (subject === 'packages') {
          parsedSubject = 'Packages'
        } else if (subject === 'warehouse') {
          parsedSubject = 'Warehouse'
        } else if (subject === 'products') {
          parsedSubject = 'Products'
        } else if (subject === 'customers') {
          parsedSubject = 'Customers'
        } else if (subject === 'analytics') {
          parsedSubject = 'Analytics'
        } else {
          parsedSubject = capitalizeFirst(subject)
        }
        
        const parsed = {
          action,
          subject: parsedSubject
        }
        return parsed
      }
      
      // If it doesn't match the 3-part pattern, treat as a string permission
      // This handles cases like 'some-complex-permission-name'
      return {
        action: permission
      }
    }
    
    // Handle standard permissions (format: action_resource)
    const [action, ...subjectParts] = permission.split('_')
    
    return {
      action,
      subject: subjectParts
        .map(s => capitalizeFirst(s))
        .join('')
    }
  }
  
  return permission
}

/**
 * Parse role-based permissions into CASL format
 * This function is now simplified - most permission logic should be handled by the backend
 */
export function parseRolePermissions(role, userTeam = null) {
  const permissions = []
  
  // admin and superadmin both get full CASL access on the front-end.
  // Backend gates destructive ops (DELETE, settings writes) by role, so this
  // is just for UI visibility.
  if (role === 'admin' || role === 'superadmin') {
    permissions.push({ action: 'manage', subject: 'all' })
  }
  
  return permissions
}

/**
 * Convert Laravel permission to CASL format
 */
export function laravelToCasl(laravelPermission) {
  // Handle spatie/laravel-permission format
  if (laravelPermission.includes('.')) {
    const [action, subject] = laravelPermission.split('.')
    return {
      action,
      subject: capitalizeFirst(subject)
    }
  }
  
  return parsePermission(laravelPermission)
}

/**
 * Generate simple CASL rules from user data
 */
export function generateUserAbilityRules(user) {
  const rules = []

  if (!user) {
    return rules
  }

  const { roles = [], permissions = [] } = user

  const roleNameOf = role => (typeof role === 'object' ? role?.name : role)
  const hasRole = name => roles.some(r => roleNameOf(r) === name)

  // admin and superadmin both get full CASL access — backend role guards
  // are what actually protect destructive endpoints.
  if (hasRole('admin') || hasRole('superadmin')) {
    rules.push({ action: 'manage', subject: 'all' })
    return rules
  }

  // Money / accounting clerk — needs read-only access to the warehouse
  // pages so they can navigate to a group and confirm payment via the
  // workflow chip → PAYER. Backend re-validates every transition; this
  // is just UI visibility. The route guard reads `meta.action / subject`
  // so we provide rules that satisfy the warehouse + invoice routes.
  //
  // (Earlier we tried to centralise this in createAbility() — that's
  // dead code on this branch; login.vue calls generateUserAbilityRules
  // directly. So the rules live here.)
  if (hasRole('money')) {
    rules.push(
      { action: 'read', subject: 'Dashboard' },
      { action: 'read', subject: 'Warehouse' },
      { action: 'read', subject: 'Customers' },
      { action: 'read', subject: 'Parcels' },
      { action: 'read', subject: 'Packages' },
      { action: 'read', subject: 'PackingLists' },
      { action: 'read', subject: 'Containers' },
      { action: 'read', subject: 'Analytics' },
    )
    // Don't return — fall through so direct `permissions` (e.g.
    // `mark-group-paid`) get appended too if Spatie surfaces them.
  }

  // For non-admin users, use backend permissions directly
  permissions.forEach(permission => {
    const permissionName = typeof permission === 'object' ? permission.name : permission
    const parsedRule = parsePermission(permissionName)
    rules.push(parsedRule)
  })

  return rules
}

/**
 * Helper function to capitalize first letter
 */
function capitalizeFirst(str) {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

// Team constants removed - now fully dynamic based on backend permissions

/**
 * Common CASL subjects
 */
export const CASL_SUBJECTS = {
  ALL: 'all',
  TEAMS: 'Teams',
  USER_TEAMS: 'UserTeams',
  PACKAGES: 'Packages',
  PARCEL_STATUS: 'ParcelStatus',
  PARCEL_IMAGES: 'ParcelImages',
  TEAM_ANALYTICS: 'TeamAnalytics',
  ALL_ANALYTICS: 'AllAnalytics',
  CLIENT_ORDERS: 'ClientOrders',
  CLIENT_PURCHASES: 'ClientPurchases',
  CLIENT_ANALYTICS: 'ClientAnalytics',
  CROSS_TEAM_PACKAGES: 'CrossTeamPackages',
  CUSTOMERS: 'Customers'
}

/**
 * Common CASL actions
 */
export const CASL_ACTIONS = {
  MANAGE: 'manage',
  CREATE: 'create',
  READ: 'read',
  UPDATE: 'update',
  DELETE: 'delete',
  VIEW: 'view',
  ACCESS: 'access',
  SCAN: 'scan',
  UPLOAD: 'upload',
  PROCESS: 'process',
  ASSIGN: 'assign'
}
